"use client";

import * as React from "react";
import { FinalAssignment, AssignmentType } from "../types";

/* -------------------------
   Derived helpers
------------------------- */

function buildExamHelpers(exams: FinalAssignment[]) {
  return {
    byCourse(courseId: string) {
      return exams.filter((e) => e.course_id === courseId);
    },

    upcoming(from = new Date()) {
      return exams.filter(
        (e) => e.due_at && new Date(e.due_at) >= from && !e.submitted
      );
    },

    overdue(now = new Date()) {
      return exams.filter(
        (e) =>
          e.due_at &&
          new Date(e.due_at) < now &&
          !e.submitted &&
          !e.marked_complete
      );
    },

    byType(type: AssignmentType) {
      return exams.filter((e) => e.type === type);
    },

    completed() {
      return exams.filter((e) => e.submitted || e.marked_complete || e.graded);
    },

    isExam: (exam: FinalAssignment) =>
      exam.type === AssignmentType.QUIZ ||
      ["quiz", "test", "exam", "midterm", "final", "assessment"].some((x) =>
        exam.name.toLowerCase().includes(x)
      ),
  };
}

/* -------------------------
   Context types
------------------------- */

type ExamsContextValue = {
  exams: FinalAssignment[];
  setExams: React.Dispatch<React.SetStateAction<FinalAssignment[]>>;

  helpers: ReturnType<typeof buildExamHelpers>;
};

/* -------------------------
   Context
------------------------- */

const ExamsContext = React.createContext<ExamsContextValue | null>(null);

/* -------------------------
   Provider
------------------------- */

export function ExamsProvider({
  initialExams = [],
  children,
}: {
  initialExams?: FinalAssignment[];
  children: React.ReactNode;
}) {
  const [exams, setExams] = React.useState<FinalAssignment[]>(initialExams);

  const helpers = React.useMemo(() => buildExamHelpers(exams), [exams]);

  const value = React.useMemo(
    () => ({
      exams,
      setExams,
      helpers,
    }),
    [exams, helpers]
  );

  return (
    <ExamsContext.Provider value={value}>{children}</ExamsContext.Provider>
  );
}

/* -------------------------
   Hook
------------------------- */

export function useExams() {
  const ctx = React.useContext(ExamsContext);
  if (!ctx) {
    throw new Error("useExams must be used within an ExamsProvider");
  }
  return ctx;
}
